import { Component, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { delay, map, Subscription } from 'rxjs';

import { updateSavedProperty } from '../data-access/+state/university.actions';
import { getStateUniversity } from '../data-access/+state/university.selectors';
import {
	Gallery,
	Product,
	ProductEnum,
	UniversityProfile,
	UniversitySchema,
} from '@uc/web/shared/data-models';
import { UniversityUtils } from '../utils/university.utils';
import { isStoreEmpty, RemoveDuplicateDomElementDirective } from '@uc/utilities';
import {
	ClientFeatureHandler,
	JsonLdSchemaService,
	UniversityReadUrlService,
	getDetailByProduct,
} from '@uc/web/core';
import { AsyncPipe } from '@angular/common';
import { NavigationTabsComponent } from '../components/navigation-tabs/navigation-tabs.component';
import {
	ProspectusBtnComponent,
	RequestBtnComponent,
	UniversityHeaderComponent,
	VisitWebsiteBtnComponent,
} from '@web/shared/ui';
import { UniversityHeaderTitleComponent } from '../components/university-header-title/university-header-title.component';

@Component({
	selector: 'uc-university-shell',
	templateUrl: './university-shell.component.html',
	imports: [
		AsyncPipe,
		RouterOutlet,
		RemoveDuplicateDomElementDirective,
		NavigationTabsComponent,
		UniversityHeaderComponent,
		UniversityHeaderTitleComponent,
		VisitWebsiteBtnComponent,
		ProspectusBtnComponent,
		RequestBtnComponent,
	],
	providers: [UniversityReadUrlService],
})
export class UniversityShellComponent implements OnDestroy {
	universityUtils = new UniversityUtils();
	uniGallery!: Gallery;
	product!: Product;
	subdirectory!: string;
	queryParams = this._route.snapshot.queryParams;
	universityUrl = this._route.snapshot.params['uniName'];
	isPreview = this.queryParams['preview'] === 'true';
	clientFeatures!: ClientFeatureHandler;
	externalLink = signal('');
	externalProspectusLink = signal('');

	university$ = this._store.pipe(
		select(getStateUniversity),
		delay(1),
		map((university) => {
			if (isStoreEmpty(university)) return null;

			if (university) {
				this._jsonLdSrv.addUniversitySchema(
					this._prepareSchemaObject(university),
				);
				this.clientFeatures = new ClientFeatureHandler(
					university.paid_features,
					this.isPreview,
				);
				this._createGallery(university);

				this.externalProspectusLink.set(
					getDetailByProduct(
						university,
						'external_prospectus_url',
						this.product,
					),
				);

				this.externalLink.set(
					getDetailByProduct(university, 'external_url', this.product),
				);

				/**
				 * TODO: this implementation is not taking into consideration if the user clicks the browser back button
				 * and is creating a loop where the user is redirected to the same page
				 */
				if (
					this.clientFeatures.isPostExclusive &&
					this.product === ProductEnum.Undergraduate
				) {
					// if client is postgraduate exclusive, redirect to postgraduate landing page
					this._router.navigate([
						'/universities',
						this.universityUrl,
						'postgraduate',
					]);
				}

				this._removePreviewQueryParam(university.paid_features);
			}

			return university;
		}),
	);

	private _productSub!: Subscription;
	private _subdirectorySub!: Subscription;

	constructor(
		private _store: Store,
		private _route: ActivatedRoute,
		private _router: Router,
		private _readUrlSrv: UniversityReadUrlService,
		private _jsonLdSrv: JsonLdSchemaService,
	) {
		this._productSub = this._readUrlSrv
			.getProduct$()
			.subscribe((response) => (this.product = response));
		this._subdirectorySub = this._readUrlSrv
			.getSubdirectory()
			.subscribe((subdirectory) => (this.subdirectory = subdirectory));
	}

	ngOnDestroy(): void {
		this._jsonLdSrv.removeSchema('university-schema');
		this._productSub?.unsubscribe();
		this._subdirectorySub?.unsubscribe();
	}

	/**
	 * TODO: the call of this function has been removed. To be reimplemented
	 */
	onUpdateSavedInStore(saved: boolean | number | undefined): void {
		if (saved === undefined) return;
		/**
		 * TODO: issues with the API alignment
		 * GraphQL API returns a boolean
		 * PHP API returns a number
		 */
		const toBoolean = saved === 1;
		this._store.dispatch(updateSavedProperty({ saved: toBoolean }));
	}

	private _createGallery(uni: UniversityProfile): void {
		if (!uni || !uni.universityGallery) return;
		const coverImage = getDetailByProduct(uni, 'cover_image', this.product);

		this.uniGallery = {
			cover_image: coverImage,
			images: uni.universityGallery.images,
		};
	}

	// removes the preview from the url if the client is not in preview mode
	private _removePreviewQueryParam(paidFeatures: string[]): void {
		const isPreviewMode = this.universityUtils.isPreviewMode(
			paidFeatures,
			this.isPreview,
		);
		if (!this.clientFeatures.isPostExclusive && !isPreviewMode) {
			const queryParams = { ...this.queryParams, preview: null };
			this._router.navigate([], {
				relativeTo: this._route,
				queryParams: this.subdirectory === 'reviews' ? queryParams : {},
			});
			this.isPreview = false;
		}
	}

	private _prepareSchemaObject(university: UniversityProfile): UniversitySchema {
		return {
			name: university.name,
			region: university.address.region,
			address: university.address.street,
			postcode: university.address.postcode,
			rating: university.stats.rating,
			reviewsCount: university.stats.reviews_count,
		};
	}
}
